import type { NavigationConfig, NavSectionConfig, NavItemConfig } from './types';
import { iconRegistry } from './icons';

export class NavigationConfigError extends Error {
  constructor(message: string, public path: string) {
    super(`[Navigation Config Error] ${path}: ${message}`);
    this.name = 'NavigationConfigError';
  }
}

/**
 * Validates icon key exists in registry
 */
function validateIconKey(key: string, path: string): void {
  if (!(key in iconRegistry)) {
    throw new NavigationConfigError(
      `Unknown icon key "${key}". Available: ${Object.keys(iconRegistry).join(', ')}`,
      path
    );
  }
}

/**
 * Validates a navigation item
 */
function validateNavItem(item: NavItemConfig, path: string): void {
  if (!item.id || typeof item.id !== 'string') {
    throw new NavigationConfigError('Item must have a string "id"', path);
  }
  if (!item.label || typeof item.label !== 'string') {
    throw new NavigationConfigError('Item must have a string "label"', `${path}.${item.id}`);
  }
  if (!item.href || typeof item.href !== 'string') {
    throw new NavigationConfigError('Item must have a string "href"', `${path}.${item.id}`);
  }
  if (!item.href.startsWith('/')) {
    throw new NavigationConfigError(
      `href "${item.href}" must start with "/" for internal routes`,
      `${path}.${item.id}`
    );
  }
  if (typeof item.order !== 'number') {
    throw new NavigationConfigError('Item must have a numeric "order"', `${path}.${item.id}`);
  }
  if (!Array.isArray(item.roles)) {
    throw new NavigationConfigError('Item must have a "roles" array', `${path}.${item.id}`);
  }
  validateIconKey(item.icon, `${path}.${item.id}.icon`);
}

/**
 * Validates a navigation section
 */
function validateNavSection(section: NavSectionConfig, path: string): void {
  if (!section.id || typeof section.id !== 'string') {
    throw new NavigationConfigError('Section must have a string "id"', path);
  }
  if (typeof section.order !== 'number') {
    throw new NavigationConfigError('Section must have a numeric "order"', `${path}.${section.id}`);
  }
  if (!Array.isArray(section.roles)) {
    throw new NavigationConfigError('Section must have a "roles" array', `${path}.${section.id}`);
  }
  if (!Array.isArray(section.items)) {
    throw new NavigationConfigError('Section must have an "items" array', `${path}.${section.id}`);
  }

  // Validate all items
  section.items.forEach((item, index) => {
    validateNavItem(item, `${path}.${section.id}.items[${index}]`);
  });

  // Check for duplicate item IDs within section
  const itemIds = section.items.map((item) => item.id);
  const duplicates = itemIds.filter((id, i) => itemIds.indexOf(id) !== i);
  if (duplicates.length > 0) {
    throw new NavigationConfigError(
      `Duplicate item IDs found: ${duplicates.join(', ')}`,
      `${path}.${section.id}`
    );
  }
}

/**
 * Validates the entire navigation configuration.
 * Should be called at app startup.
 * @throws NavigationConfigError if validation fails
 */
export function validateNavigationConfig(config: NavigationConfig): void {
  if (!config || typeof config !== 'object') {
    throw new NavigationConfigError('Config must be an object', 'root');
  }
  if (!Array.isArray(config.sections)) {
    throw new NavigationConfigError('Config must have a "sections" array', 'root');
  }

  // Validate all sections
  config.sections.forEach((section, index) => {
    validateNavSection(section, `sections[${index}]`);
  });

  // Check for duplicate section IDs
  const sectionIds = config.sections.map((s) => s.id);
  const duplicates = sectionIds.filter((id, i) => sectionIds.indexOf(id) !== i);
  if (duplicates.length > 0) {
    throw new NavigationConfigError(
      `Duplicate section IDs found: ${duplicates.join(', ')}`,
      'sections'
    );
  }

  // Collect all item IDs across sections for global uniqueness check
  const allItemIds: string[] = [];
  config.sections.forEach((section) => {
    section.items.forEach((item) => {
      if (allItemIds.includes(item.id)) {
        throw new NavigationConfigError(
          `Duplicate item ID "${item.id}" found across sections`,
          'global'
        );
      }
      allItemIds.push(item.id);
    });
  });
}

